import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";

import {
  LOCK_TYPE,
  categoryDropdownConstants,
  EMPTY_STRING,
  inventoryConstants,
  routesPathConstants,
  defaultGridStateDataTable,
  PAGE_END_RECORD,
} from "../../../constants";
import Topbar from "../../../shared/components/topbar/Topbar";
import DataTableComponent from "../../../shared/components/table/ReactDataTableComponent";
import {
  getStockAuditBreakdownExpandedRow,
  getStockAuditBreakdownColumns,
  convertExpandedDataToDisplayable,
  getStockAuditBreakdownGridData,
  getStockAuditCSVWithSummary,
} from "../../../shared/utilities/columnHeadersUtility/stockAudit";
import { inventoryActions } from "../../../redux/actions";

import {
  getCustomCategoriesOptionList,
  getCustomSubCategoriesOptionList,
  getCustomTypesOptionList,
  downloadCSV,
  isSpinnerEnabled,
  LoadingIndicatorLarge,
  formatDate,
} from "../../../shared/utility";
import {
  BlueBoxIcon,
  CrossIcon,
  DownloadIcon,
  EditIcon,
  ExportPrintIcon,
  SearchIcon,
  TickIcon,
} from "../../../assets/icons/iconsProvider";
import {
  CategoriesDropdown,
  SearchField,
  SimpleInputTextField,
} from "../../../shared/components";
import SimpleTextArea from "../../../shared/components/inputTextArea/SimpleTextArea";
import {
  BREAKDOWN_ENUMS,
  PRODUCT_TYPES_ENUMS,
} from "../../../system/globalEnums";
import { useLocation } from "react-router";
import { useDebouncedCallback } from "use-debounce";
import { useReactToPrint } from "react-to-print";

import { inventoryService } from "../../../services";
import reportLogo from "../../../assets/images/reportLogo.jpg";
import AuditSummary from "./components/AuditBreakdownSummary";

const StockAuditBreakdown = (props) => {
  const {
    itemOrganization,
    stockAuditBreakdown,
    getStockAuditBreakdown,
    updateStockAuditBreakdown,
    currentStore,
    spinnerArray,
  } = props;
  const { customTypes, customCategories, customSubCategories } =
    itemOrganization;
  const { state } = useLocation();
  const { currentAudit } = state || {};
  const stockAudit = stockAuditBreakdown?.stockAudit || {};

  const expandedData = convertExpandedDataToDisplayable(stockAudit);
  const [isEditNickName, setIsEditNickName] = useState(false);
  const [nickName, setNickName] = useState(stockAudit?.nickName || "");
  const [isEditNotes, setIsEditNotes] = useState(false);
  const [notes, setNotes] = useState(stockAudit?.notes || "");
  const [auditBreakdown, setAuditBreakdown] = useState(BREAKDOWN_ENUMS.TYPE);
  const [searchText, setSearchText] = useState(EMPTY_STRING);
  const [selectedType, setSelectedType] = useState({});
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedSubCategory, setSelectedSubCategory] = useState({});
  const [gridState, setGridState] = useState({
    ...defaultGridStateDataTable,
    searchQuery: EMPTY_STRING,
    filters: EMPTY_STRING,
    sortFilter: EMPTY_STRING,
  });
  const [defaultSortFieldId, setDefaultSortFieldId] = useState();
  const [columns, setColumns] = useState(
    getStockAuditBreakdownColumns(auditBreakdown.value)
  );
  const shouldShowExpandedRow =
    auditBreakdown.value !== BREAKDOWN_ENUMS.ITEM.value;
  const gridData = useMemo(() => {
    return getStockAuditBreakdownGridData(
      stockAuditBreakdown,
      auditBreakdown.value
    );
  }, [stockAuditBreakdown, auditBreakdown.value]);
  const { stockAuditStatistics } = stockAuditBreakdown || {};
  const [expandedRowId, setExpandedRowId] = useState(null);
  const componentRef = useRef();

  const printFunction = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Audit Breakdown",
  });
  const handleDownload = async () => {
    if (!currentStore?.id || !currentAudit?.id) return;
    try {
      const filters = {
        selectedProductType: selectedType?.label,
        selectedCategory: selectedCategory?.label,
        selectedSubCategory: selectedSubCategory?.label,
      };

      const response = await inventoryService.getStockAuditBreakdown(
        currentStore?.id,
        currentAudit?.id,
        auditBreakdown.value,
        { ...gridState, pageSize: PAGE_END_RECORD, pageIndex: 1 },
        filters
      );

      const allData =
        auditBreakdown.value === BREAKDOWN_ENUMS.ITEM.value
          ? response.inventory
          : response.stockAuditItemOrganization;

      const csvData = getStockAuditCSVWithSummary(
        auditBreakdown.value,
        allData,
        response.stockAudit,
        response.stockAuditStatistics
      );

      downloadCSV(csvData, `AuditBreakdown-${auditBreakdown.label}`);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const handleRowClick = (row) => {
    // const rowId = getRowId(row);
    setExpandedRowId(expandedRowId === row?.id ? null : row?.id);
  };

  //changing the sort on table
  const onTableSort = (column, sortDirection) => {
    setGridState((prevState) => ({
      ...prevState,
      sortFilter: `${column.id} ${sortDirection.toUpperCase()}`,
    }));
    setColumns(
      columns.map((col) => {
        if (col.id === column.id) {
          return {
            ...col,
            sortDirection,
          };
        } else {
          return {
            ...col,
            sortDirection: null,
          };
        }
      })
    );
    setDefaultSortFieldId(column.id);
  };

  // changing the nickname clicking edit
  const handleEditNicknameClick = () => {
    setIsEditNickName(true);
  };
  // changing the nickname clicking confirm
  const handleTickNicknameClick = () => {
    updateStockAuditBreakdown(currentStore?.id, currentAudit?.id, {
      ...stockAudit,
      nickName,
    }).then(() => {
      if (currentStore?.id && currentAudit?.id) {
        const filters = {
          selectedProductType: selectedType?.label,
          selectedCategory: selectedCategory?.label,
          selectedSubCategory: selectedSubCategory?.label,
        };

        getStockAuditBreakdown(
          currentStore?.id,
          currentAudit?.id,
          auditBreakdown.value,
          { ...gridState, pageIndex: gridState.pageIndex + 1 },
          filters
        );
      }
    });
    setIsEditNickName(false);
  };
  // changing the nickname clicking cancel
  const handleCrossNicknameClick = () => {
    setIsEditNickName(false);
  };
  // changing the notes clicking edit
  const handleEditNotesClick = () => {
    setIsEditNotes(true);
  };
  // changing the notes clicking confirm
  const handleTickNotesClick = () => {
    updateStockAuditBreakdown(currentStore?.id, currentAudit?.id, {
      ...stockAudit,
      notes,
      nickName: stockAudit?.nickName || "",
    }).then(() => {
      if (currentStore?.id && currentAudit?.id) {
        const filters = {
          selectedProductType: selectedType?.label,
          selectedCategory: selectedCategory?.label,
          selectedSubCategory: selectedSubCategory?.label,
        };

        getStockAuditBreakdown(
          currentStore?.id,
          currentAudit?.id,
          auditBreakdown.value,
          { ...gridState, pageIndex: gridState.pageIndex + 1 },
          filters
        );
      }
    });

    setIsEditNotes(false);
  };
  // changing the notes clicking cancel
  const handleCrossNotesClick = () => {
    setIsEditNotes(false);
  };

  const handleDebouncedSearch = useDebouncedCallback((value) => {
    setGridState((prev) => ({ ...prev, searchQuery: value, pageIndex: 0 }));
  }, 600);

  const clearSearchText = () => {
    setSearchText(() => EMPTY_STRING);
    setGridState((prev) => ({
      ...prev,
      searchQuery: EMPTY_STRING,
      pageIndex: 0,
    }));
  };
  const handleTypeChange = (selectedOption) => {
    setSelectedType(selectedOption || EMPTY_STRING);
    setSelectedCategory(EMPTY_STRING);
    setSelectedSubCategory(EMPTY_STRING);
    setGridState((prev) => ({ ...prev, pageIndex: 0 }));
  };
  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption || EMPTY_STRING);
    setSelectedSubCategory(EMPTY_STRING);
    setGridState((prev) => ({ ...prev, pageIndex: 0 }));
  };
  const handleSubCategoryChange = (selectedOption) => {
    // If selectedOption is null (cleared), set it to EMPTY_STRING
    const newSelectedSubCategory = selectedOption || EMPTY_STRING;
    setSelectedSubCategory(newSelectedSubCategory);
    setGridState((prev) => ({ ...prev, pageIndex: 0 }));
  };

  useEffect(() => {
    setNickName(stockAudit?.nickName || "");
    setNotes(stockAudit?.notes || "");
  }, [stockAudit?.nickName, stockAudit?.notes]);

  useEffect(() => {
    setColumns(getStockAuditBreakdownColumns(auditBreakdown.value));
    // reset filters and search when breakdown changes
    setSelectedType(EMPTY_STRING);
    setSelectedCategory(EMPTY_STRING);
    setSelectedSubCategory(EMPTY_STRING);
    setSearchText(EMPTY_STRING);
    setExpandedRowId(null);
    setGridState((prev) => ({
      ...prev,
      pageIndex: 0,
      searchQuery: EMPTY_STRING,
    }));
  }, [auditBreakdown]);
  const typeOptions = useMemo(() => {
    return (
      getCustomTypesOptionList(
        (customTypes || []).filter(
          (t) => t.productType !== PRODUCT_TYPES_ENUMS.ALL
        )
      ) || []
    );
  }, [customTypes]);

  useEffect(() => {
    if (currentAudit?.id) {
      const filters = {
        selectedProductType: selectedType?.label,
        selectedCategory: selectedCategory?.label,
        selectedSubCategory: selectedSubCategory?.label,
      };

      getStockAuditBreakdown(
        currentStore?.id,
        currentAudit?.id,
        auditBreakdown.value,
        { ...gridState, pageIndex: gridState.pageIndex + 1 },
        filters
      );
    }
  }, [
    currentStore?.id,
    currentAudit?.id,
    auditBreakdown.value,
    gridState,
    selectedType,
    selectedCategory,
    selectedSubCategory,
    getStockAuditBreakdown,
  ]);

  const categoryOptions = useMemo(() => {
    const selectedTypeId = selectedType?.value || null;
    return (
      getCustomCategoriesOptionList(customCategories || [], selectedTypeId) ||
      []
    );
  }, [customCategories, selectedType]);

  const subCategoryOptions = useMemo(() => {
    const selectedTypeId = selectedType?.value || null;
    const selectedCategoryId = selectedCategory?.value || null;
    return (
      getCustomSubCategoriesOptionList(
        customSubCategories || [],
        selectedCategoryId,
        null,
        selectedTypeId,
        false,
        customCategories || [],
        customTypes || []
      ) || []
    );
  }, [
    customSubCategories,
    customCategories,
    customTypes,
    selectedType,
    selectedCategory,
  ]);
  const ExpandedRowComponent = useCallback(
    (rowProps) => {
      const Component = getStockAuditBreakdownExpandedRow;

      return <Component {...rowProps} breakdownBy={auditBreakdown.value} />;
    },
    [auditBreakdown.value]
  );

  const skeletonArray = Array(10).fill({});
  return (
    <>
      <Topbar
        title="Audit Breakdown"
        backNavigation={{
          isVisible: true,
          route: routesPathConstants.INVENTORY_ITEMS_STOCK_AUDIT,
        }}
      >
        <button
          className="action-button-wrapper border-0 bg-transparent"
          onClick={printFunction}
        >
          <ExportPrintIcon className="cursor-pointer color-dark-blue" />
        </button>
        <button
          className="action-button-wrapper border-0 bg-transparent"
          onClick={handleDownload}
        >
          <DownloadIcon className="cursor-pointer color-dark-blue " />
        </button>
      </Topbar>

      {currentStore.inventoryOperationsLockType ===
      LOCK_TYPE.UPDATE_INVENTORY_AUDIT ? (
        <>
          <div className="d-flex flex-column justify-content-center align-items-center vh-100">
            <LoadingIndicatorLarge size="lg" />
            <span className="audit-label mt-4">
              Preparing Breakdown for the Audit.
            </span>
          </div>
        </>
      ) : (
        <>
          <AuditSummary isPrintView={false}>
            <AuditSummary.BasicInfo stockAudit={stockAudit} isPrintView={false}>
              <Row className="mb-3 d-flex align-items-center">
                <Col md={3}>
                  <span className="predefinded-audit-label">Nickname:</span>
                </Col>
                <Col md={9}>
                  <Row className="align-items-center g-2">
                    <Col md={isEditNickName ? 10 : 11}>
                      <SimpleInputTextField
                        isDisabled={!isEditNickName}
                        value={
                          isEditNickName ? nickName : stockAudit?.nickName || ""
                        }
                        placeHolder="Enter NickName"
                        wrapperClassName={"w-100"}
                        onChange={(e) => setNickName(e.target.value)}
                        isSmall={true}
                      />
                    </Col>
                    <Col
                      md={isEditNickName ? 2 : 1}
                      className="d-flex justify-content-center"
                    >
                      {isEditNickName ? (
                        <div className="d-flex gap-2">
                          <button
                            className="action-Button-Wrapper ps-0 pe-0"
                            onClick={handleCrossNicknameClick}
                          >
                            <CrossIcon className="cursor-pointer" />
                          </button>
                          <button
                            className="action-Button-Wrapper ps-0 pe-0"
                            onClick={handleTickNicknameClick}
                          >
                            <TickIcon className="cursor-pointer text-primary" />
                          </button>
                        </div>
                      ) : (
                        <button
                          className="action-Button-Wrapper ps-0 pe-0"
                          onClick={handleEditNicknameClick}
                        >
                          <EditIcon className="cursor-pointer text-muted" />
                        </button>
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </AuditSummary.BasicInfo>

            <AuditSummary.Statistics
              auditBreakdownValue={auditBreakdown.value}
              stockAuditBreakdown={stockAuditBreakdown}
              stockAuditStatistics={stockAuditStatistics}
              isPrintView={false}
            />

            <AuditSummary.Kpi expandedData={expandedData} isPrintView={false} />

            <AuditSummary.Notes>
              <Row>
                <Col
                  md={12}
                  className="mb-3 d-flex align-items-center justify-content-between"
                >
                  <span className="predefinded-audit-label">Notes</span>
                  {isEditNotes ? (
                    <div className="mt-1 d-flex gap-2">
                      <button
                        className="action-Button-Wrapper ps-0 pe-0"
                        onClick={handleCrossNotesClick}
                      >
                        <CrossIcon className="cursor-pointer" />
                      </button>
                      <button
                        className="action-Button-Wrapper ps-0 pe-0"
                        onClick={handleTickNotesClick}
                      >
                        <TickIcon className="cursor-pointer text-primary" />
                      </button>
                    </div>
                  ) : (
                    <button
                      className="action-Button-Wrapper ps-0 pe-0"
                      onClick={handleEditNotesClick}
                    >
                      <EditIcon className="cursor-pointer text-muted" />
                    </button>
                  )}
                </Col>
              </Row>
              <Row>
                <Col md={12} className="d-flex flex-column">
                  <SimpleTextArea
                    isDisabled={!isEditNotes}
                    value={isEditNotes ? notes : stockAudit?.notes || ""}
                    onChange={(e) => setNotes(e.target.value)}
                    placeHolder="Enter Notes"
                    maxLength={1000}
                  />
                </Col>
              </Row>
            </AuditSummary.Notes>
          </AuditSummary>
          <Row className="m-0 mt-4 mb-4 mx-2">
            <Col md={3} className="d-flex align-items-center gap-3">
              <span className="audit-label">Breakdown By:</span>
              <CategoriesDropdown
                options={inventoryConstants.BREAKDOWN_OPTIONS}
                value={auditBreakdown}
                handleChange={(selectedOption) => {
                  setAuditBreakdown(selectedOption || EMPTY_STRING);
                }}
                placeholder="Type"
                SelectIconImage={categoryDropdownConstants.DEFAULT_ICON}
                isClearable={false}
                wrapperClassname="w-100"
              />
            </Col>
          </Row>
          <Row className="m-0 mt-2 mb-1 mx-2">
            <Col md={6}>
              <SearchField
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchText(value);
                  handleDebouncedSearch(value);
                }}
                IconImage={SearchIcon}
                placeHolder={
                  auditBreakdown.value === BREAKDOWN_ENUMS.TYPE.value
                    ? "Search Type Name..."
                    : auditBreakdown.value === BREAKDOWN_ENUMS.CATEGORY.value
                    ? "Search Category Name..."
                    : auditBreakdown.value === BREAKDOWN_ENUMS.SUBCATEGORY.value
                    ? "Search Subcategory Name..."
                    : "Search Item Name / SKU / UPC..."
                }
                value={searchText}
                CrossIcon={searchText ? CrossIcon : ""}
                clearSearchText={clearSearchText}
                BlueBoxIcon={BlueBoxIcon}
              />
            </Col>
            <Col md={6}>
              <Row>
                <Col md={3}>
                  {auditBreakdown.value !== BREAKDOWN_ENUMS.TYPE.value && (
                    <CategoriesDropdown
                      placeholder="Type"
                      options={typeOptions}
                      value={selectedType}
                      noOptionsMessage="No Type Found"
                      SelectIconImage={categoryDropdownConstants.DEFAULT_ICON}
                      handleChange={handleTypeChange}
                    />
                  )}
                </Col>
                <Col md={3}>
                  {[
                    BREAKDOWN_ENUMS.SUBCATEGORY.value,
                    BREAKDOWN_ENUMS.ITEM.value,
                  ].includes(auditBreakdown.value) && (
                    <CategoriesDropdown
                      placeholder="Category"
                      options={categoryOptions}
                      value={selectedCategory}
                      noOptionsMessage="No Category Found"
                      SelectIconImage={categoryDropdownConstants.DEFAULT_ICON}
                      handleChange={handleCategoryChange}
                    />
                  )}
                </Col>
                <Col md={3}>
                  {auditBreakdown.value === BREAKDOWN_ENUMS.ITEM.value && (
                    <CategoriesDropdown
                      placeholder="Subcategory"
                      options={subCategoryOptions}
                      value={selectedSubCategory}
                      noOptionsMessage="No Subcategory Found"
                      SelectIconImage={categoryDropdownConstants.DEFAULT_ICON}
                      handleChange={handleSubCategoryChange}
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="m-0 w-100 h-100 user-screen-wrapper mx-2 mt-1">
            <div
              className="w-100 h-100 stock-audit-print-area"
              ref={componentRef}
            >
              <div className="noPrint pt-5">
                <div>
                  <div className="d-flex flex-column ">
                    {formatDate(currentAudit?.unconvertedData?.startDate)}
                    {currentAudit?.unconvertedData?.endDate
                      ? ` - ${formatDate(
                          currentAudit?.unconvertedData?.endDate
                        )}`
                      : ""}
                  </div>
                  <span className="sale-summary-report-heading">
                    Audit Breakdown
                  </span>
                </div>
                <img src={reportLogo} width="30px" alt="logo" />
              </div>
              <div className="pt-3 pb-2 d-none show-on-print">
                <AuditSummary isPrintView={true}>
                  <AuditSummary.BasicInfo
                    stockAudit={stockAudit}
                    isPrintView={true}
                  />

                  <AuditSummary.Statistics
                    auditBreakdownValue={auditBreakdown.value}
                    stockAuditBreakdown={stockAuditBreakdown}
                    stockAuditStatistics={stockAuditStatistics}
                    isPrintView={true}
                  />

                  <AuditSummary.Kpi
                    expandedData={expandedData}
                    isPrintView={true}
                  />
                </AuditSummary>
              </div>
              <Row className="mt-3 w-100 user-screen-wrapper pt-3">
                <DataTableComponent
                  className="stock-audit-breakdown-table"
                  columns={columns}
                  data={
                    isSpinnerEnabled(spinnerArray, [
                      inventoryConstants.GET_STOCK_AUDIT,
                    ])
                      ? skeletonArray
                      : gridData
                  }
                  keyField="id"
                  expandableRows={shouldShowExpandedRow}
                  expandableRowsHideExpander={true}
                  expandableRowsComponent={ExpandedRowComponent}
                  expandableRowsComponentProps={{
                    breakdownBy: auditBreakdown.value,
                    stockAudit: stockAudit,
                  }}
                  onRowClicked={handleRowClick}
                  expandedRowId={expandedRowId}
                  sortServer
                  onSort={onTableSort}
                  defaultSortFieldId={defaultSortFieldId}
                  paginationServer={true}
                  gridState={gridState}
                  setGridState={setGridState}
                  total={stockAuditBreakdown?.totalRecords ?? 0}
                  onChangePage={() => {}}
                  onChangeRowsPerPage={() => {}}
                />
              </Row>
            </div>
          </Row>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  spinnerArray: state.system.spinnerArray,
  currentStore: state.store.currentStore,
  itemOrganization: state.itemOrganization,
  stockAuditBreakdown: state.inventory.stockAuditBreakdown,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getStockAuditBreakdown: (
    storeId,
    auditId,
    breakdownBy,
    gridState,
    filterIds
  ) =>
    dispatch(
      inventoryActions.getStockAuditBreakdown(
        storeId,
        auditId,
        breakdownBy,
        gridState,
        filterIds
      )
    ),
  updateStockAuditBreakdown: (storeId, auditId, data) =>
    dispatch(
      inventoryActions.updateStockAuditBreakdown(storeId, auditId, data)
    ),
});

//-------Export AddInvoice Component
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StockAuditBreakdown);
